import React, { Fragment, useEffect, useState, ChangeEvent } from 'react';
import {
  Box, Button, Card, Dialog, Grid, MenuItem, Select, Table,
  TableBody, TableContainer, TableHead, TablePagination, TableRow,
  Typography, FormControl, InputLabel, TextField, InputAdornment
} from '@mui/material';
import { withStyles } from '@mui/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditIcon from '@mui/icons-material/Edit';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import SearchIcon from '@mui/icons-material/Search';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { TableColumnCell } from 'src/components/TableColumnCell';
import AddDesignation from './AddDesignation';
import EditDesignation from './EditDesignation';
import { getAllDesignations } from 'src/services/DesignationService';
import { IDesignations } from './interface/designation';
import { ETierOptions } from './enum/ETierOptions';
import { ETrackOptions } from './enum/ETrackOptions';
import SnackBar from 'src/components/SnackBar/SnackBar';

const CustomTableRow = withStyles({
  root: {
    background: '#E6E9FF !important',
    height: '3.5rem'
  }
})(TableRow);

const CustomBodyRow = withStyles({
  root: {
    height: '3.0rem'
  }
})(TableRow);

const applyPagination = (data: IDesignations[], page: number, limit: number): IDesignations[] => {
  return data.slice(page * limit, page * limit + limit);
};

const tierOptions = Object.values(ETierOptions);
const trackOptions = Object.values(ETrackOptions);

const DesignationView: React.FC = () => {
  const [designations, setDesignations] = useState<IDesignations[]>([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [editForm, setEditForm] = useState(false);
  const [selectedDesignation, setSelectedDesignation] = useState<IDesignations | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterValues, setFilterValues] = useState({ code: '', tier: '', track: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isClearingFilters, setIsClearingFilters] = useState(false);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [totalCount, setTotalCount] = useState(0);


  const loadDesignations = async () => {
    try {
      const filter: any = {
        pageNo: page,        // use current page number
        pageSize: limit,     // use current rows per page
        ...(filterValues.tier && { tier: [filterValues.tier] }),
        ...(filterValues.track && { track: [filterValues.track] }),
        ...(searchTerm && { name: searchTerm })
      };

      const res = await getAllDesignations(filter);

      let list = res?.data?.data?.designations ?? [];
      const total = res?.data?.data?.totalCount ?? list.length;

      setDesignations(list);
      setTotalCount(total); // set full count here
    } catch (error) {
      console.error('Error fetching designations:', error);
      setSnackBarMessage('Failed to load designations.');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
    }
  };

  useEffect(() => {
    const hasAnyFilter = filterValues.tier || filterValues.track;
    setIsFilterActive(!!hasAnyFilter);
  }, [filterValues]);

  useEffect(() => {
    if (searchTerm) {
      setPage(0);
    }
    loadDesignations();
  }, [searchTerm, page, limit]);

  const handlePageChange = (_: any, newPage: number) => setPage(newPage);
  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value);
    setLimit(newLimit);
    setPage(0);
  };

  const clearAdvancedFilters = () => {
    setFilterValues({ code: '', tier: '', track: '' });
    setPage(0);
    setIsClearingFilters(true);   
  };

  useEffect(() => {
    if (isClearingFilters) {
      loadDesignations();
      setIsClearingFilters(false);
    }
  }, [filterValues, isClearingFilters]);

  const handleSuccess = (msg: string) => {
    setSnackBarMessage(msg);
    setSnackbarSeverity('success');
    setSnackBarOpen(true);
  };

  const handleError = (msg: string) => {
    setSnackBarMessage(msg);
    setSnackbarSeverity('error');
    setSnackBarOpen(true);
  };

  return (
    <Fragment>
      <Box sx={{ px: 4 }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>
            Admin Configurations / <span style={{ fontSize: '14px', fontWeight: 500 }}>Designations</span>
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddTwoToneIcon fontSize="small" />}
            onClick={() => setOpenForm(true)}
            sx={{ fontSize: '12px', height: '2rem', minWidth: '140px' }}
          >
            Create New
          </Button>
        </Grid>

        <Card sx={{ px: 2, py: 2, mb: 2 }}>
          <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Designation Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#d3d3d3' }} style={{ fontSize: '15px' }} />
                    </InputAdornment>
                  ),
                  style: { height: '1.75rem', fontSize: '12px' }
                }}
              />

            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon style={{ fontSize: '15px' }} />}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{ fontSize: '12px', height: '1.75rem', minWidth: '100px' }}
              >
                Filters
              </Button>

            </Grid>
          </Grid>

          {showAdvancedFilters && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <InputLabel
                    sx={{
                      fontSize: '12px',
                      top: '-6px'  // 🔼 Shift label text up
                    }}
                  >
                    Tier
                  </InputLabel>
                  <Select
                    value={filterValues.tier}
                    onChange={(e) => setFilterValues({ ...filterValues, tier: e.target.value })}
                    label="Tier"
                    sx={{
                      height: '1.75rem',
                      fontSize: '12px'
                    }}
                  >
                    {tierOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>


              </Grid>
              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <InputLabel
                    sx={{
                      fontSize: '12px',
                      top: '-6px'  // Shift "Track" up
                    }}
                  >
                    Track
                  </InputLabel>
                  <Select
                    value={filterValues.track}
                    onChange={(e) => setFilterValues({ ...filterValues, track: e.target.value })}
                    label="Track"
                    sx={{
                      height: '1.75rem',
                      fontSize: '12px'
                    }}
                  >
                    {trackOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} container justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={clearAdvancedFilters}
                  disabled={!isFilterActive}
                  sx={{
                    fontSize: '12px',
                    height: '2rem',
                    mr: 1,
                    borderRadius: '8px',
                    textTransform: 'none',
                    borderColor: isFilterActive ? '#4D6AFF' : '#c4c4c4',
                    color: isFilterActive ? '#4D6AFF' : '#6f7a8a',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: isFilterActive ? 'rgba(77, 106, 255, 0.05)' : 'transparent',
                    },
                    '&.Mui-disabled': {
                      borderColor: '#c4c4c4',
                      color: '#6f7a8a',
                      fontWeight: 'bold'
                    }
                  }}
                >
                  Clear
                </Button>

                <Button
                  variant="outlined"
                  onClick={loadDesignations}
                  disabled={!isFilterActive}
                  sx={{
                    fontSize: '12px',
                    height: '2rem',
                    borderRadius: '8px',
                    textTransform: 'none',
                    borderColor: isFilterActive ? '#4D6AFF' : '#c4c4c4',
                    color: isFilterActive ? '#4D6AFF' : '#6f7a8a',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: isFilterActive ? 'rgba(77, 106, 255, 0.05)' : 'transparent',
                    },
                    '&.Mui-disabled': {
                      borderColor: '#c4c4c4',
                      color: '#6f7a8a',
                      fontWeight: 'bold'
                    }
                  }}
                >
                  Filter
                </Button>
              </Grid>

            </Grid>
          )}

<TableContainer>
  <Table size="small" style={{ borderCollapse: 'separate', tableLayout: 'auto' }}>
    <TableHead sx={{ backgroundColor: '#e6e9ff' }}>
      <CustomTableRow>
        <TableHeaderCell content="Name" smallFont />
        <TableHeaderCell content="Code" smallFont />
        <TableHeaderCell content="Tier" smallFont />
        <TableHeaderCell content="Track" smallFont />
        <TableHeaderCell content="Department" smallFont />
        <TableHeaderCell content="Actions" smallFont sticky />
      </CustomTableRow>
    </TableHead>
    <TableBody>
      {designations.map((item) => (
        <CustomBodyRow key={item.id} hover>
          <TableColumnCell>{item.name}</TableColumnCell>
          <TableColumnCell>{item.code}</TableColumnCell>
          <TableColumnCell>{item.tier}</TableColumnCell>
          <TableColumnCell>{item.track}</TableColumnCell>
          <TableColumnCell>{item.departmentName}</TableColumnCell>
          <TableColumnCell sticky>
            <EditIcon
                sx={{
                  fontSize: '17px',
                  cursor: 'pointer',
                  color: 'text.enabled',
                }}
                onClick={() => {
                  setSelectedDesignation(item);
                  setEditForm(true);
                }}                    
              />
          </TableColumnCell>
        </CustomBodyRow>
      ))}
      {designations.length === 0 && (
        <CustomBodyRow>
          <TableColumnCell colSpan={6}>
            <Typography
              style={{
                fontSize: '15px',
                fontWeight: 'bold',
                margin: '1rem',
                textAlign: 'center',
                color: '#1a3365'
              }}
              variant="body2"
            >
              No Records Found
            </Typography>
          </TableColumnCell>
        </CustomBodyRow>
      )}
    </TableBody>
  </Table>
</TableContainer>

          <Box p={2}>
            <TablePagination
              component="div"
              count={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              page={page}
              rowsPerPage={limit}
              rowsPerPageOptions={[10, 20, 30, 40, 50]}
            />
          </Box>
        </Card>

        <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
          <AddDesignation
            onClose={() => setOpenForm(false)}
            onSubmit={() => {
              clearAdvancedFilters();
              loadDesignations();
              handleSuccess('Designation created successfully.');
            }}
          />
        </Dialog>

        <Dialog open={editForm} onClose={() => setEditForm(false)} maxWidth="sm" fullWidth>
          {selectedDesignation && (
            <EditDesignation
              key={selectedDesignation.id}
              data={selectedDesignation}
              onClose={() => setEditForm(false)}
              onSubmit={() => {
                loadDesignations();
                handleSuccess('Designation updated successfully.');
              }}
            />
          )}
        </Dialog>

        <SnackBar
          snackBarOpen={snackBarOpen}
          setSnackBarOpen={setSnackBarOpen}
          snackbarSeverity={snackbarSeverity}
          snackBarMessage={snackBarMessage}
        />
      </Box>
    </Fragment>
  );
};

export default DesignationView;
